import { useRef, useState } from 'react'

import { useMountEffect } from '@/hooks/useMountEffect'

/**
 * Detects whether an element's content overflows horizontally (i.e. is
 * visually truncated by `truncate` / `text-overflow: ellipsis`).
 * Re-evaluates on element resize. Attach the returned ref to the element
 * that carries the `truncate` class.
 */
export function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useMountEffect(() => {
    const checkTruncation = () => {
      if (ref.current) {
        const { scrollWidth, clientWidth } = ref.current
        setIsTruncated(scrollWidth > clientWidth)
      }
    }

    const resizeObserver = new ResizeObserver(checkTruncation)

    if (ref.current) {
      resizeObserver.observe(ref.current)
    }

    checkTruncation()

    return () => {
      resizeObserver.disconnect()
    }
  })

  return { ref, isTruncated }
}
